/**
 * 작성자 : 홍선영
 * 날짜 : 2023.07.19
 * 기능 : 근로자 1명의 일별 출역리스트 모달
 */

import { Dispatch, SetStateAction, useState, useRef, useEffect } from 'react';
import { useRecoilValue } from 'recoil';

import { userState } from '../../atoms';
import { Modal, ModalBackground } from '../../assets/styles/Modal';
import { setComCdListState } from '../../utils/setComCdListState';
import { LIST_COUNT } from '../../_constants';
import { IComCdList, IModal } from 'customTypes';
import { useReactToPrint } from 'react-to-print';
import TuiGrid from '../Table/TuiGrid';
import { arraySortByAscdOrder } from '../../utils/arraySortByAscdOrder';
import SelectBox from '../SelectBox';
import { useTranslation } from 'react-i18next';
import i18n from '../../translation/i18n';
import TuiGridWrapper from '../Table/TuiGridWrapper';
import { apiGet } from '../../services/_common';
import useTuiGridSize from '../../hooks/useTuiGridSize';
import { isMobile } from '../../utils/checkMobile';
import { resetMobileViewportAfterKeyboard } from '../../utils/resetMobileViewportAfterKeyboard';

interface ISearchOption {
  sjName: string;
  sjCd: string;
  startDate: string;
  endDate: string;
  wName?: string;
  name?: string;
  partCd?: string;
  partName?: string;
}

interface IProps {
  setOpenModal: Dispatch<SetStateAction<IModal>>;
  filter: ISearchOption;
  dGubun?: 'c';
}

const AttendDailyPersonalListModal = ({ setOpenModal, filter, dGubun }: IProps) => {
  const { t } = useTranslation();
  const userInfo = useRecoilValue(userState);
  const [dailyPersonalAttendState, setDailyPersonalAttendState] = useState([]);
  const { eListYn } = userInfo;
  const [columns, setColumns] = useState<any[]>([]);
  const [listCountComCdList, setListCountComCdList] = useState<IComCdList[]>([]); // 검색줄수 공통코드
  const [listCount, setListCount] = useState({
    type: LIST_COUNT,
    [LIST_COUNT]: '15',
    cdName: '15',
  });
  const [firstApiReq, setFirstApiReq] = useState(false);
  const [faceSetList, setFaceSetList] = useState<any[]>([]);
  const isMobileView = isMobile();
  const componentRef = useRef<HTMLDivElement>(null);
  const { tuiHeight } = useTuiGridSize(componentRef);
  const isCompanyMobileView = dGubun === 'c' && isMobileView;

  useEffect(() => {
    getFaceSetAPI().then((res: any) => {
      if (res.status === 200) {
        setComCdListState(LIST_COUNT, setListCountComCdList, false);
        setListCount({ LIST_COUNT: '15', cdName: '15', type: 'LIST_COUNT' });
        setFirstApiReq(true);
      }
    });
  }, []);

  // 근로자 검색 키패드가 올라온 상태에서 모달 진입 시 팝업이 작게 뜨는 문제 방지
  useEffect(() => {
    if (!isMobileView) return undefined;
    resetMobileViewportAfterKeyboard();
    return undefined;
  }, []);

  useEffect(() => {
    if (isCompanyMobileView) {
      setListCount({ LIST_COUNT: '15', cdName: '15', type: LIST_COUNT });
    }
  }, [isCompanyMobileView]);

  useEffect(() => {
    if (dGubun === 'c') {
      setColumns(
        isMobileView
          ? [
              {
                header: t('출역시간'),
                name: 'aTime',
                align: 'center',
                sortable: true,
                minWidth: 76,
                renderer: { classNames: ['text_secondary'] },
              },
              {
                header: t('출입위치'),
                name: 'fLocationName',
                align: 'center',
                sortable: true,
                filter: 'select',
                minWidth: 92,
              },
              {
                header: t('장비명'),
                name: 'fName',
                align: 'center',
                sortable: true,
                minWidth: 76,
              },
              {
                header: t('체온'),
                name: 'temp',
                align: 'center',
                sortable: true,
                minWidth: 56,
              },
            ]
          : [
              {
                header: t('근로자명'),
                name: 'name',
                sortable: true,
                minWidth: 130,
                renderer: { classNames: ['text_secondary'] },
              },
              {
                header: t('부서명'),
                name: 'partName',
                sortable: true,
                minWidth: 130,
                renderer: { classNames: ['text_secondary'] },
              },
              {
                header: t('직급명'),
                name: 'positionName',
                sortable: true,
                minWidth: 130,
                renderer: { classNames: ['text_secondary'] },
              },
              {
                header: t('출역일자'),
                name: 'aDate',
                align: 'center',
                sortable: true,
                filter: 'select',
                minWidth: 120,
                renderer: { classNames: ['text_secondary'] },
              },
              {
                header: t('출역시간'),
                name: 'aTime',
                align: 'center',
                sortable: true,
                minWidth: 120,
                renderer: { classNames: ['text_secondary'] },
              },
              {
                header: t('장비명'),
                name: 'fName',
                sortable: true,
                minWidth: 120,
              },
              {
                header: t('출입위치'),
                name: 'fLocationName',
                sortable: true,
                filter: 'select',
                minWidth: 160,
              },
              {
                header: t('체온'),
                name: 'temp',
                align: 'center',
                sortable: true,
                minWidth: 120,
              },
              {
                header: t('알코올 농도'),
                name: 'wAlcohol',
                align: 'center',
                sortable: true,
                minWidth: 120,
              },
            ]
      );
    } else {
      setColumns(
        userInfo.prejobtypeYn === 'Y'
          ? [
              {
                header: t('근로자명'),
                name: 'wName',
                sortable: true,
                isPrivate: true,
                maskingType: 'name',
                minWidth: 130,
                renderer: { classNames: ['text_secondary'] },
              },
              {
                header: t('협력업체'),
                name: 'sjName',
                sortable: true,
                filter: 'select',
                width: 200,
                renderer: { classNames: ['text_secondary'] },
              },
              {
                header: t('공종'),
                name: 'wPrejobtypeName',
                sortable: true,
                minWidth: 120,
              },
              {
                header: t('직종'),
                name: 'wJobtypeName',
                sortable: true,
                minWidth: 120,
              },
              {
                header: t('취업일자'),
                name: 'wJobdate',
                align: 'center',
                sortable: true,
                minWidth: 120,
              },
              {
                header: t('생년월일'),
                name: 'wBdate',
                align: 'center',
                sortable: true,
                minWidth: 120,
              },
              {
                header: t('출역일자'),
                name: 'aDate',
                align: 'center',
                sortable: true,
                filter: 'select',
                minWidth: 120,
                renderer: { classNames: ['text_secondary'] },
              },
              {
                header: t('출역시간'),
                name: 'aTime',
                align: 'center',
                sortable: true,
                minWidth: 120,
                renderer: { classNames: ['text_secondary'] },
              },
              {
                header: t('장비명'),
                name: 'fName',
                sortable: true,
                minWidth: 120,
              },
              {
                header: t('출입위치'),
                name: 'fLocationName',
                sortable: true,
                filter: 'select',
                minWidth: 160,
              },
              {
                header: t('체온'),
                name: 'temp',
                align: 'center',
                sortable: true,
                minWidth: 120,
              },
              {
                header: t('알코올 농도'),
                name: 'wAlcohol',
                align: 'center',
                sortable: true,
                minWidth: 120,
              },
            ]
          : [
              {
                header: t('근로자명'),
                name: 'wName',
                align: 'center',
                sortable: true,
                isPrivate: true,
                maskingType: 'name',
                minWidth: 130,
              },
              {
                header: t('협력업체'),
                name: 'sjName',
                align: 'center',
                sortable: true,
                width: 200,
              },

              {
                header: t('직종'),
                name: 'wJobtypeName',
                sortable: true,
                width: 120,
                renderer: { classNames: ['text_secondary'] },
              },
              {
                header: t('취업일자'),
                name: 'wJobdate',
                sortable: true,
                minWidth: 120,
                renderer: { classNames: ['text_secondary'] },
              },
              {
                header: t('생년월일'),
                name: 'wBdate',
                align: 'center',
                sortable: true,
                minWidth: 120,
              },
              {
                header: t('출역일자'),
                name: 'aDate',
                align: 'center',
                sortable: true,
                filter: 'select',
                minWidth: 120,
                renderer: { classNames: ['text_secondary'] },
              },
              {
                header: t('출역시간'),
                name: 'aTime',
                align: 'center',
                sortable: true,
                minWidth: 120,
                renderer: { classNames: ['text_secondary'] },
              },
              {
                header: t('장비명'),
                name: 'fName',
                align: 'center',
                sortable: true,
                minWidth: 120,
              },
              {
                header: t('출입위치'),
                name: 'fLocationName',
                align: 'center',
                sortable: true,
                filter: 'select',
                minWidth: 120,
              },
              {
                header: t('체온'),
                name: 'temp',
                align: 'center',
                sortable: true,
                minWidth: 120,
              },
              {
                header: t('알코올 농도'),
                name: 'wAlcohol',
                align: 'center',
                sortable: true,
                minWidth: 120,
              },
            ]
      );
    }
  }, [userInfo.prejobtypeYn, i18n.language, dGubun, isMobileView]);

  useEffect(() => {
    if (firstApiReq) {
      getAttendDetailAPI();
      setFirstApiReq(false);
    }
  }, [firstApiReq]);

  const onClickClose = () => {
    setOpenModal((prev) => ({ ...prev, status: false }));
  };

  // 개인별 일 출역현황 조회 API
  const getAttendDetailAPI = async () => {
    const path = dGubun !== 'c' ? '/attend/dayDetail' : '/attend/dayDetail/company';
    const newSearchOption = { ...filter, aDate1: filter.startDate.replaceAll('-', ''), aDate2: filter.endDate.replaceAll('-', '') };
    const { sjName, startDate, endDate, aDate1, aDate2, ...finalSearchObj } = newSearchOption; // searchOption에서 start, end값 삭제
    const req = dGubun !== 'c' ? { ...finalSearchObj, aDate1, aDate2, hCd: userInfo.hCd, sCd: userInfo.sCd } : { aDate1, aDate2, cCd: userInfo.hCd, partCd: filter.partCd, name: newSearchOption.name };
    const res = await apiGet({ path, req });
    const { data, statusCode } = res.data;
    if (statusCode === 200) {
      if (eListYn === 'Y') {
        const arr = data.attendList.map((v: any) => {
          const item = faceSetList.find((v2: any) => v2.fNum === v.fNum);
          return {
            ...v,
            fName: item?.name,
            fLocationName: item?.fLocationName,
          };
        });
        setDailyPersonalAttendState(arr);
      } else {
        setDailyPersonalAttendState(data.attendList);
      }
    } else {
      // toast.error(t(ERROR));
    }
  };

  // 장비 리스트 조회 API (리스트)
  const getFaceSetAPI = async () => {
    const req = { hCd: userInfo.hCd, sCd: userInfo.sCd };
    const res = await apiGet({ path: '/setting/faceSet', req });
    const { data, statusCode } = res.data;
    if (statusCode === 200) {
      const newArray: any = data.faceSet.map((el: any, i: number) => {
        return { type: 'fNum', fNum: el.fNum, name: el.fName, cdSort: i + 1, fLocation: el.fLocation, fLocationName: el.fLocationName, fInout: el.fInout };
      });
      const sortedArray: any = arraySortByAscdOrder(newArray, 'cdSort');
      setFaceSetList(sortedArray);
    } else {
      // toast.error(t(ERROR));
    }
    return res;
  };

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });
  // 인쇄 버튼 클릭
  const onClickPrint = () => {
    handlePrint();
  };

  return (
    <ModalBackground>
      <Modal>
        <div className='modal minHeight-609 dailyPersonalAttend'>
          <div className='inputForm-head'>
            <div className='modalHeaderTitle'>{t('개인별 일 출역현황')}</div>
            <div className='closeBtn' onClick={onClickClose} role='presentation'>
              <span className='material-symbols-rounded'>close</span>
            </div>
          </div>
          <div className='inputForm-body dailyPersonalAttend'>
            <div className='flex-between searchWrapper'>
              {!isCompanyMobileView && (
                <div className='flex-basic view-option'>
                  <label htmlFor='listCount'>{t('보기 설정')}</label>
                  <SelectBox options={listCountComCdList} defaultOption={listCount.cdName} state={listCount} setState={setListCount} initiateKey={listCount.cdName} rsearch />
                </div>
              )}
              <div className='detail-info'>
                {!isCompanyMobileView &&
                  (dGubun !== 'c' ? (
                    <div className='flex-basic'>
                      <label htmlFor='sjCd'>{t('협력업체')}</label>
                      <span className='seperator' />
                      <span className='data'>{filter.sjName}</span>
                    </div>
                  ) : (
                    <div className='flex-basic'>
                      <label htmlFor='partCd'>{t('부서명')}</label>
                      <span className='seperator' />
                      <span className='data'>{filter.partName}</span>
                    </div>
                  ))}
                <div className='flex-basic'>
                  <label htmlFor='date'>{t('출역일자')}</label>
                  <span className='seperator' />
                  <div className='flex-basic'>
                    <span className='data'>{filter.startDate}</span>
                    <span className='wave'> ~ </span>
                    <span className='data'>{filter.endDate}</span>
                  </div>
                </div>
                {!isCompanyMobileView && filter.wName && (
                  <div className='flex-basic'>
                    <label htmlFor='wName'>{t('근로자명')}</label>
                    <input type='text' value={filter.wName} maxLength={50} />
                  </div>
                )}
                {/* <BtnGray onClick={onClickPrint}>인쇄</BtnGray> */}
              </div>
            </div>
            <TuiGridWrapper componentRef={componentRef} scrollYHidden={!isCompanyMobileView}>
              <TuiGrid
                data={dailyPersonalAttendState}
                columns={columns}
                perPage={isCompanyMobileView ? 15 : Number(listCount[LIST_COUNT])}
                usePagenation
                frozenCount={isCompanyMobileView ? 0 : 1}
                height={tuiHeight}
                eListYn={eListYn}
                scrollX={!isCompanyMobileView}
                idxKey={isCompanyMobileView ? 'not use' : undefined}
              />
            </TuiGridWrapper>
            {/* </div> */}
          </div>
        </div>
      </Modal>
    </ModalBackground>
  );
};

export default AttendDailyPersonalListModal;
